import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { ChartFullscreenModalComponent } from '../chart-fullscreen-modal/chart-fullscreen-modal';
import * as am5 from '@amcharts/amcharts5';
import {
  createCategoryAxis,
  createChartRoot,
  createColumnSeriesCollection,
  createToggleLegend,
  createValueAxis,
  createXYChart,
  SharedColumnSeriesDefinition,
} from '../../Charts/amcharts-xy';
import { Installation as InstallationService, ModeAffichageGraphique } from '../../Services/installation';
import { SpinnerOverlayComponent } from '../spinner-overlay/spinner-overlay';
import { ChartPeriodSelectorComponent, PeriodeChangee } from '../chart-period-selector/chart-period-selector';

type ChartPoint = {
  date: string;
  production: number;
  reference: number;
  autoconsommation: number;
  productibleEstime: number;
};

@Component({
  selector: 'app-installation-production-cumulee-panel',
  imports: [CommonModule, SpinnerOverlayComponent, ChartFullscreenModalComponent, ChartPeriodSelectorComponent],
  templateUrl: './installation-production-cumulee-panel.html',
  styleUrl: './installation-production-cumulee-panel.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstallationProductionCumuleePanelComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() installationId: number | null = null;
  @Input() autoconso = false;
  @ViewChild('chartHost', { static: true }) private chartHostRef?: ElementRef<HTMLDivElement>;

  private platformId = inject(PLATFORM_ID);
  private installationService = inject(InstallationService);
  private cdr = inject(ChangeDetectorRef);
  private chartRoot?: am5.Root;
  private viewInitialized = false;
  private requeteId = 0;
  private chartData: ChartPoint[] = [];

  mode: ModeAffichageGraphique = 'mois';
  dateFin = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  chargement = false;
  messageGraphique = '';
  menuOuvert = false;
  modalVisible = false;
  modalRefreshToken = 0;

  readonly modesDisponibles: ModeAffichageGraphique[] = ['mois', 'annee'];

  readonly cumuleeChartRenderer = (host: HTMLDivElement): am5.Root => {
    return this.creerGraphique(host, this.chartData, this.mode);
  };

  @HostListener('document:click')
  fermerMenu() {
    this.menuOuvert = false;
    this.cdr.markForCheck();
  }

  toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    this.menuOuvert = !this.menuOuvert;
  }

  ouvrirModal() {
    this.menuOuvert = false;
    this.modalVisible = true;
    this.cdr.markForCheck();
  }

  onPeriodeChange({ mode, dateFin }: PeriodeChangee) {
    this.mode = mode;
    this.dateFin = dateFin;
    if (this.installationId != null) {
      void this.chargerEtRenderiser(this.installationId);
    }
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    if (this.installationId != null) {
      void this.chargerEtRenderiser(this.installationId);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['installationId']) {
      ++this.requeteId;
      this.chargement = false;
      this.messageGraphique = '';
      this.renderiserGraphique([]);
    }
    if (changes['installationId'] && !changes['installationId'].firstChange && this.viewInitialized) {
      this.mode = 'mois';
      this.dateFin = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      if (this.installationId != null) {
        void this.chargerEtRenderiser(this.installationId);
      }
    }

    if (changes['autoconso'] && !changes['autoconso'].firstChange && this.viewInitialized) {
      this.renderiserGraphique(this.chartData);
    }
  }

  ngOnDestroy() {
    ++this.requeteId;
    this.chartRoot?.dispose();
  }

  private get productionCumuleeRange() {
    return this.installationService.getProductionCumuleeRange(this.mode, this.dateFin);
  }

  private async chargerEtRenderiser(id: number) {
    const requete = ++this.requeteId;
    const estValide = () => requete === this.requeteId && id === this.installationId;
    this.chargement = true;
    this.messageGraphique = '';
    this.cdr.markForCheck();
    try {
      const data = await this.avecTimeout(
        this.installationService.getIndicateursProductionCumulee(
          id,
          this.productionCumuleeRange.debutApi,
          this.productionCumuleeRange.format
        ),
        10000
      );
      if (!estValide()) return;
      const points = this.mapperDonnees(data);
      this.messageGraphique = points.length === 0
        ? 'Les donnees de production cumulee sont indisponibles pour le moment.'
        : '';
      this.renderiserGraphique(points);
    } catch (error) {
      if (!estValide()) return;
      console.error('Erreur chargement production cumulée', error);
      this.messageGraphique = 'Impossible de charger les donnees de production cumulee.';
      this.renderiserGraphique([]);
    } finally {
      if (estValide()) {
        this.chargement = false;
        this.cdr.markForCheck();
      }
    }
  }

  private async avecTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        promise,
        new Promise<T>((_, reject) => {
          timeoutId = setTimeout(() => reject(new Error('Timeout chargement production cumulee')), timeoutMs);
        }),
      ]);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  private mapperDonnees(data: unknown): ChartPoint[] {
    const lignes = this.extraireLignes(data);
    const points: ChartPoint[] = [];

    for (const item of lignes) {
      if (!item || typeof item !== 'object') continue;
      const ligne = item as Record<string, unknown>;

      const dateStr = String(ligne['date'] ?? ligne['jour'] ?? ligne['periode'] ?? ligne['debut'] ?? '');
      if (!dateStr) continue;

      const production = Number(ligne['production'] ?? ligne['production_cumulee'] ?? ligne['prod'] ?? 0);
      const reference = Number(ligne['prod_calc'] ?? ligne['reference'] ?? ligne['reference_cumulee'] ?? ligne['ref'] ?? 0);
      const autoconsommation = Number(
        ligne['autoconsommation'] ?? ligne['auto_consommation'] ?? ligne['autoconso'] ?? 0
      );
      const productibleEstime = Number(
        ligne['productible_estime'] ?? ligne['productibleEstime']
        ?? ligne['prod_calc'] ?? ligne['reference'] ?? ligne['reference_cumulee'] ?? ligne['ref'] ?? 0
      );

      points.push({ date: dateStr, production, reference, autoconsommation, productibleEstime });
    }

    return points;
  }

  private extraireLignes(data: unknown): unknown[] {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      if (Array.isArray(obj['datas'])) return obj['datas'];
      if (Array.isArray(obj['data'])) return obj['data'];
      if (Array.isArray(obj['items'])) return obj['items'];
    }
    return [];
  }

  private renderiserGraphique(chartData: ChartPoint[]) {
    this.chartData = chartData;
    const host = this.chartHostRef?.nativeElement;
    if (!host || !isPlatformBrowser(this.platformId)) return;
    this.chartRoot?.dispose();
    if (chartData.length === 0) {
      this.chartRoot = undefined;
      return;
    }
    this.chartRoot = this.creerGraphique(host, chartData, this.mode);
    if (this.modalVisible) {
      this.modalRefreshToken++;
    }
  }

  private creerGraphique(host: HTMLDivElement, chartData: ChartPoint[], mode: ModeAffichageGraphique): am5.Root {
    const root = createChartRoot(host);
    const chart = createXYChart(root, {
      paddingTop: 10,
      paddingBottom: 8,
      paddingLeft: 6,
      paddingRight: 6,
    });
    const xAxis = createCategoryAxis(root, chart, chartData, {
      categoryField: 'date',
      minGridDistance: 55,
    });
    xAxis.get('renderer').labels.template.adapters.add('text', (_text, target) => {
      const category = target.dataItem?.get('category' as never) as string | undefined;
      if (!category) return _text;
      const d = new Date(category);
      if (isNaN(d.getTime())) return _text;
      return mode === 'annee'
        ? d.toLocaleDateString('fr-FR', { month: 'short' })
        : String(d.getUTCDate());
    });
    const yAxis = createValueAxis(root, chart, {
      min: 0,
      extraMax: 0.05,
      minGridDistance: 36,
    });

    yAxis.get('renderer').grid.template.setAll({ strokeOpacity: 0.12 });
    yAxis.children.unshift(
      am5.Label.new(root, {
        text: 'Production cumulée (MWh)',
        fontSize: 11,
        fill: am5.color(0x5d645f),
        rotation: -90,
        y: am5.percent(50),
        centerX: am5.percent(50),
      })
    );

    const productionSeries: SharedColumnSeriesDefinition<ChartPoint> = {
      name: 'Production',
      categoryXField: 'date',
      valueYField: 'production',
      color: 0x4b86ff,
      yAxis,
      tooltipText: `{name}\n{categoryX}: {valueY}`,
      widthPercent: this.autoconso ? 42 : 58,
    };

    const autoconsommationSeries: SharedColumnSeriesDefinition<ChartPoint> = {
      name: 'Autoconsommation',
      categoryXField: 'date',
      valueYField: 'autoconsommation',
      color: 0xffb84d,
      yAxis,
      tooltipText: `{name}\n{categoryX}: {valueY}`,
      widthPercent: 42,
    };

    const productibleEstimeSeries: SharedColumnSeriesDefinition<ChartPoint> = {
      name: 'Productible estimé',
      categoryXField: 'date',
      valueYField: 'productibleEstime',
      color: 0x67c8b2,
      yAxis,
      tooltipText: `{name}\n{categoryX}: {valueY}`,
      widthPercent: this.autoconso ? 42 : 58,
    };

    const seriesDefinitions: SharedColumnSeriesDefinition<ChartPoint>[] = this.autoconso
      ? [productionSeries, autoconsommationSeries, productibleEstimeSeries]
      : [productionSeries, productibleEstimeSeries];

    const series = createColumnSeriesCollection(root, chart, xAxis, chartData, seriesDefinitions);
    createToggleLegend(root, chart, series, { marginTop: 10 });
    chart.appear(600, 100);
    return root;
  }
}
